import { useState, useMemo, useEffect } from 'react';

import type { CrosshairClickEvent, CrosshairValue } from './TimeSeriesChart';
import type { Range } from './providers/RangeSelectionProvider';
import type { PlotRenderer, PlotUtilitiesFn } from './renderers/types';
import { exists } from './utilities/utils';
import { getSeriesKey } from './renderers/uplot-utils';

// uPlot uses -10,-10 coordinates to represent "cursor off" state
const CURSOR_OFF_POSITION = { left: -10, top: -10 } as const;

export type CursorState = {
    state: 'off' | 'hover';
    cursor: {
        /** left in pixels, relative to `cursorEl` */
        left: number;
        /** top in pixels, relative to `cursorEl` */
        top: number;
        /** time index */
        index: number;
    } | null;
    /** selection */
    selection: {
        left: number;
        width: number;
    } | null;
    /** series index */
    seriesIndex: number | null;
    /** active plot that user is interacting with */
    active: boolean;
};

type CursorPluginFactoryOptions = {
    id: string;
    plotUtilitiesFn: PlotUtilitiesFn;
    plotRenderer: PlotRenderer;
    // Element to attach to for cursor movements
    cursorEl?: HTMLElement;
    // Scale factor for coordinate calculations
    scaleFactor: number;
    // Callback for internal state change events
    onChange: (state: CursorState) => void;
    // Callback for crosshair change events; only published by "active" chart
    onCrosshairChange?: (crosshairValue?: CrosshairValue) => void;
    // Callback for click events
    onCrosshairClick?: (event: CrosshairClickEvent) => void;
    // Callback for range selection events
    onRangeSelection?: (range: Range, e: MouseEvent) => void;
};

type CursorPluginFactoryResponse = {
    setCrosshairValue: (crosshairValue?: CrosshairValue) => void;
    cleanupEventListeners: () => void;
    setupEventListeners: () => void;
};

export function cursorPluginFactory({
    id,
    plotUtilitiesFn,
    plotRenderer,
    cursorEl,
    scaleFactor,
    onChange,
    onCrosshairChange,
    onCrosshairClick,
    onRangeSelection,
}: CursorPluginFactoryOptions): CursorPluginFactoryResponse {
    let state: CursorState = {
        state: 'off',
        cursor: null,
        selection: null,
        seriesIndex: null,
        active: false,
    };

    // Use requestAnimationFrame throttling instead of debounce for smoother response
    let isFrameUpdateScheduled = false;
    let latestCursorState: CursorState | null = null;

    const fireOnChange = (nextState: CursorState, immediate = false) => {
        latestCursorState = nextState;

        if (immediate) {
            // Flush immediately for critical state changes (e.g., mouseleave)
            isFrameUpdateScheduled = false;
            onChange(nextState);
            latestCursorState = null;
        } else if (!isFrameUpdateScheduled) {
            isFrameUpdateScheduled = true;
            requestAnimationFrame(() => {
                isFrameUpdateScheduled = false;
                if (latestCursorState) {
                    onChange(latestCursorState);
                    latestCursorState = null;
                }
            });
        }
    };

    // mutate state and publish change
    function change(nextState: Partial<CursorState>, immediate = false): void {
        state = Object.assign(state, nextState);
        fireOnChange(state, immediate);
    }

    // Fires a change event to onCrosshairChange, converting `cursor` to `crosshairValue`
    function fireCrosshairChange(cursor?: CursorState['cursor']): void {
        const plotUtilities = plotUtilitiesFn();
        if (!onCrosshairChange || !plotUtilities) {
            return;
        }

        if (!cursor) {
            onCrosshairChange(undefined);
            return;
        }

        if (!state.active) {
            return;
        }

        const seriesKey = getSeriesKey(state.seriesIndex, plotUtilities.allSeries);
        const time = plotUtilities.posToVal(cursor.left, 'x');

        onCrosshairChange({
            time,
            seriesKey,
            index: cursor.index,
            seriesIndex: state.seriesIndex ?? undefined,
        });
    }

    // A returned callback function that can be used by consumers to pass crosshairValue changes
    // to this component.
    function setCrosshairValue(crosshairValue?: CrosshairValue): void {
        const plotUtilities = plotUtilitiesFn();

        // If this is the active chart, we don't handle the event as this was the chart that
        // published the event
        if (state.active || !plotUtilities) {
            return;
        }

        if (crosshairValue) {
            plotUtilities.setCursor(
                {
                    left: plotUtilities.valToPos(crosshairValue.time, 'x'),
                    // using `undefined` top will prevent uplot from highlighting a "closest series"
                    top: undefined as unknown as number,
                },
                true
            );
        } else {
            // Turn off cursor; uPlot uses {-10,-10} to represent the "off" state
            plotUtilities.setCursor(CURSOR_OFF_POSITION, true);
        }
    }

    function fireRangeSelection(e: MouseEvent, selection?: CursorState['selection']): void {
        const plotUtilities = plotUtilitiesFn();
        if (
            !state.active ||
            !plotUtilities ||
            !onRangeSelection ||
            !selection ||
            !exists(selection.left) ||
            !exists(selection.width)
        ) {
            return;
        }

        const start = selection.width > 0 ? selection.left : selection.left - selection.width;
        const end = selection.width > 0 ? selection.left + selection.width : selection.left;

        const startTime = plotUtilities.posToVal(start, 'x');
        const endTime = plotUtilities.posToVal(end, 'x');

        // Skip invalid selections where start >= end to avoid propagating bad time ranges to drilldown handlers.
        if (startTime >= endTime) {
            return;
        }

        onRangeSelection(
            {
                start: startTime,
                end: endTime,
                vizId: id,
            },
            e
        );
    }

    // Track mouse activity
    const mouseenter = (): void => {
        change({ active: true }, true); // immediate update
    };
    const mouseleave = (): void => {
        const plotUtilities = plotUtilitiesFn();
        change({ active: false, cursor: null }, true); // immediate update to prevent race conditions
        plotUtilities?.setCursor(CURSOR_OFF_POSITION, true);
        fireCrosshairChange();
    };

    // Cursor state
    let clientX: number | undefined;
    let clientY: number | undefined;
    let selecting = false;

    // Mouse event handlers
    const mousedown = (e: MouseEvent): void => {
        clientX = e.clientX / scaleFactor;
        clientY = e.clientY / scaleFactor;
    };

    const mouseup = (e: MouseEvent): void => {
        const plotUtilities = plotUtilitiesFn();
        const adjustedClientX = e.clientX / scaleFactor;
        const adjustedClientY = e.clientY / scaleFactor;

        // clicked in-place
        if (onCrosshairClick && adjustedClientX === clientX && adjustedClientY === clientY) {
            const { seriesIndex, cursor } = state;

            if (plotUtilities && cursor && exists(seriesIndex)) {
                onCrosshairClick({
                    time: plotUtilities.posToVal(cursor.left, 'x'),
                    index: cursor.index,
                    seriesIndex,
                    originalEvent: e,
                    seriesKey: plotUtilities.allSeries[seriesIndex].field,
                });
            }
        }

        // selected range
        if (selecting && exists(clientX) && exists(clientY)) {
            selecting = false;
            fireRangeSelection(e, state.selection);
            change({
                selection: null,
            });
        }

        // cleanup
        clientX = undefined;
        clientY = undefined;
    };

    const mousemove = (e: MouseEvent): void => {
        const plotUtilities = plotUtilitiesFn();
        if (!cursorEl || !plotUtilities) {
            return;
        }

        // Get fresh dimensions on each mouse move to handle scale changes
        // overRect is updated by uPlot lifecycle hooks when scale factor changes
        const overRect = plotUtilities.dimensions.over;

        if (exists(clientX) && exists(clientY)) {
            selecting = true;

            // Hide tooltip when range selection starts
            if (!state.selection) {
                plotUtilities.setCursor(CURSOR_OFF_POSITION, true);
                change({
                    state: 'off',
                    cursor: null,
                });
            }

            // Support moving cursor left or right, apply position offsets from interactiveEl to plotEl,
            // and constrain sizes to the plot dimension
            // Apply scaleFactor to convert from client coordinates to interactiveEl's coordinate system
            // interactiveEl is sized with overRect dimensions divided by scaleFactor
            const scaledClientX = e.clientX / scaleFactor;
            const scaledOverRectLeft = overRect.left / scaleFactor;
            const scaledOverRectWidth = overRect.width / scaleFactor;
            const leftPos = Math.min(scaledClientX, clientX) - scaledOverRectLeft;
            const rightPos = Math.max(scaledClientX, clientX) - scaledOverRectLeft;
            const left = Math.max(0, leftPos);
            const width = Math.min(scaledOverRectWidth, rightPos - leftPos);

            change({
                selection: { left: Math.max(0, left), width: Math.max(0, width) },
            });

            // Auto-complete selection when mouse goes outside chart bounds
            const mouseRelativeX = scaledClientX - scaledOverRectLeft;
            const isMouseBeyondEdge = mouseRelativeX < 0 || mouseRelativeX > scaledOverRectWidth;

            if (isMouseBeyondEdge && width > 0) {
                // Finalize selection when dragging beyond edge
                selecting = false;
                fireRangeSelection(e, state.selection);
                change({
                    selection: null,
                });
                clientX = undefined;
                clientY = undefined;
            }
        }

        // Set the cursor in uplot, which will publish a change to cursor index
        // Skip this during selection to avoid crosshair events interfering with range selection
        if (selecting) {
            return;
        }

        // Check if mouse is within the plot area bounds using overRect
        const isWithinPlotArea =
            e.clientX >= overRect.left &&
            e.clientX <= overRect.right &&
            e.clientY >= overRect.top &&
            e.clientY <= overRect.bottom;

        if (isWithinPlotArea) {
            // Calculate position relative to overRect and scale
            const left = (e.clientX - overRect.left) / scaleFactor;
            const top = (e.clientY - overRect.top) / scaleFactor;
            plotUtilities.setCursor({ left, top }, true);
        } else {
            // Mouse is outside plot area, clear the cursor and internal state
            plotUtilities.setCursor(CURSOR_OFF_POSITION, true);
            change({ cursor: null });
        }
    };

    const setupEventListeners = (): void => {
        if (cursorEl) {
            cursorEl.addEventListener('mouseenter', mouseenter);
            cursorEl.addEventListener('mouseleave', mouseleave);
            cursorEl.addEventListener('mousedown', mousedown);
            cursorEl.addEventListener('mouseup', mouseup);
            cursorEl.addEventListener('mousemove', mousemove);
        }
    };

    plotRenderer.onSetCursor(cursor => {
        if (!cursor) {
            change({
                state: 'off',
                cursor: null,
                seriesIndex: null,
            });
        } else {
            // Update the top and left of the cursor; keep index the same
            change({
                state: 'hover',
                cursor,
            });

            fireCrosshairChange(state.cursor);
        }
    });

    plotRenderer.onFocusSeries(seriesIndex => {
        change({
            seriesIndex,
        });
    });

    const cleanupEventListeners = (): void => {
        if (cursorEl) {
            cursorEl.removeEventListener('mouseenter', mouseenter);
            cursorEl.removeEventListener('mouseleave', mouseleave);
            cursorEl.removeEventListener('mousedown', mousedown);
            cursorEl.removeEventListener('mouseup', mouseup);
            cursorEl.removeEventListener('mousemove', mousemove);
        }
    };

    return {
        // Receive input from external system
        setCrosshairValue,
        setupEventListeners,
        cleanupEventListeners,
    };
}

type UseCursorPluginParams = {
    id: string;
    plotUtilitiesFn: PlotUtilitiesFn;
    plotRenderer: PlotRenderer;
    cursorEl?: HTMLElement;
    scaleFactor: number;
    crosshairValue?: CrosshairValue;
    onCrosshairChange?: (crosshairValue?: CrosshairValue) => void;
    onCrosshairClick?: (event: CrosshairClickEvent) => void;
    onRangeSelection?: (range: Range) => void;
};

type UseCursorPluginResponse = {
    cursorState?: CursorState;
};

export const useCursorPlugin = ({
    id,
    plotUtilitiesFn,
    plotRenderer,
    cursorEl,
    scaleFactor,
    crosshairValue,
    onCrosshairChange,
    onCrosshairClick,
    onRangeSelection,
}: UseCursorPluginParams): UseCursorPluginResponse => {
    // Cursor
    const [cursorState, setCursorState] = useState<CursorState>();

    const { setCrosshairValue, setupEventListeners, cleanupEventListeners } = useMemo(
        () =>
            cursorPluginFactory({
                id,
                plotUtilitiesFn,
                plotRenderer,
                cursorEl,
                scaleFactor,
                onChange: nextCursorState => {
                    setCursorState({ ...nextCursorState });
                },
                onCrosshairChange,
                onCrosshairClick,
                onRangeSelection,
            }),
        [
            id,
            plotUtilitiesFn,
            plotRenderer,
            cursorEl,
            scaleFactor,
            onCrosshairChange,
            onCrosshairClick,
            onRangeSelection,
        ]
    );
    useEffect(() => setCrosshairValue(crosshairValue), [setCrosshairValue, crosshairValue]);
    useEffect(() => {
        setupEventListeners();
        return cleanupEventListeners;
    }, [setupEventListeners, cleanupEventListeners]);

    return { cursorState };
};
